/**
 * Custom hooks for Branch Contact management
 *
 * This file contains all the custom hooks for branch contact operations including
 * CRUD operations, form management, and business logic.
 * Following the implementation plan for independent branch system.
 */

import { useToast } from '@/hooks/use-toast';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { extractErrorMessage } from '@/lib/errorUtils';
import {
  createBranchContactFormSchema,
  type BranchContactFormSchema,
} from '@/lib/validations/branchValidation';
import { apiService } from '@/services/api';
import type {
  BranchContact,
  BranchContactFormData,
  BranchContactListResponse,
  BranchContactResponse,
  CreateBranchContactData,
  UpdateBranchContactData,
} from '@/types/branchContact.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

// Error interface for API responses
interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
  };
}

/**
 * Hook for fetching a single branch contact by ID
 */
export const useBranchContact = (contactId: string | null) => {
  return useQuery({
    queryKey: ['branchContact', contactId],
    queryFn: () =>
      apiService.get<BranchContactResponse>(`/branch-contacts/${contactId}`),
    enabled: !!contactId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching all branch contacts for a specific branch
 */
export const useBranchContacts = (
  branchId?: string,
  page = 1,
  perPage = 15
) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString(),
    ...(branchId && { branch_id: branchId }),
  });

  return useQuery({
    queryKey: ['branchContacts', branchId, page, perPage],
    queryFn: () =>
      apiService.get<BranchContactListResponse>(
        `/branch-contacts?${queryParams}`
      ),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for creating a new branch contact
 */
export const useCreateBranchContact = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (contactData: CreateBranchContactData) =>
      apiService.post<BranchContactResponse>('/branch-contacts', contactData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['branchContacts'] });
      // Invalidate queries for all branches this contact is assigned to
      if (variables.branchIds && variables.branchIds.length > 0) {
        variables.branchIds.forEach(branchId => {
          queryClient.invalidateQueries({
            queryKey: ['branchContacts', branchId],
          });
        });
      }
      toast({
        title: 'Erfolg',
        description: 'Kontaktperson wurde erfolgreich erstellt.',
      });
    },
    onError: (error: ApiError) => {
      const errorMessage = extractErrorMessage(
        error,
        'Fehler beim Erstellen der Kontaktperson.'
      );
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook for updating an existing branch contact
 */
export const useUpdateBranchContact = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBranchContactData }) =>
      apiService.put<BranchContactResponse>(`/branch-contacts/${id}`, data),
    onSuccess: (_, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: ['branchContacts'] });
      queryClient.invalidateQueries({ queryKey: ['branchContact', id] });
      // Invalidate queries for all branches this contact is assigned to
      if (data.branchIds && data.branchIds.length > 0) {
        data.branchIds.forEach(branchId => {
          queryClient.invalidateQueries({
            queryKey: ['branchContacts', branchId],
          });
        });
      }
      toast({
        title: 'Erfolg',
        description: 'Kontaktperson wurde erfolgreich aktualisiert.',
      });
    },
    onError: (error: ApiError) => {
      const errorMessage = extractErrorMessage(
        error,
        'Fehler beim Aktualisieren der Kontaktperson.'
      );
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook for deleting a branch contact
 */
export const useDeleteBranchContact = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (contactId: string) =>
      apiService.delete(`/branch-contacts/${contactId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branchContacts'] });
      toast({
        title: 'Erfolg',
        description: 'Kontaktperson wurde erfolgreich gelöscht.',
      });
    },
    onError: (error: ApiError) => {
      const errorMessage = extractErrorMessage(
        error,
        'Fehler beim Löschen der Kontaktperson.'
      );
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook for managing branch contact form state with validation
 * Following the ContactPartnerForm pattern with translations
 */
interface UseBranchContactFormProps {
  branchContact?: BranchContact;
  onSave: (data: BranchContactFormData) => void;
  isOpen: boolean;
}

export const useBranchContactForm = ({
  branchContact,
  onSave,
  isOpen,
}: UseBranchContactFormProps) => {
  const { t: tForms } = useFormsTranslation();

  // Create schema with translations
  const branchContactFormSchema = createBranchContactFormSchema(tForms);

  const form = useForm<BranchContactFormSchema>({
    resolver: zodResolver(branchContactFormSchema),
    defaultValues: {
      branchIds: [],
      language: 'de',
      function: '',
      isRepresentative: false,
      address: '',
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      phone: '',
    },
  });

  // Reset form when branchContact data changes or dialog opens
  useEffect(() => {
    if (isOpen) {
      if (branchContact) {
        // Editing existing branch contact
        // Handle data migration: ensure branchIds is an array
        const legacyContact = branchContact as BranchContact & {
          branchId?: string;
        };
        const branchIds = branchContact.branchIds
          ? branchContact.branchIds
          : legacyContact.branchId
            ? [legacyContact.branchId]
            : [];

        form.reset({
          branchIds,
          language: branchContact.language || 'de',
          function: branchContact.function || '',
          isRepresentative: branchContact.isRepresentative || false,
          address: branchContact.address || '',
          firstName: branchContact.firstName || '',
          lastName: branchContact.lastName || '',
          email: branchContact.email || '',
          mobile: branchContact.mobile || '',
          phone: branchContact.phone || '',
        });
      } else {
        // Creating new branch contact
        form.reset({
          branchIds: [],
          language: 'de',
          function: '',
          isRepresentative: false,
          address: '',
          firstName: '',
          lastName: '',
          email: '',
          mobile: '',
          phone: '',
        });
      }
    }
  }, [isOpen, branchContact, form]);

  const handleSubmit = useCallback(
    (data: BranchContactFormSchema) => {
      // Convert BranchContactFormSchema to BranchContactFormData for compatibility
      onSave(data as BranchContactFormData);
    },
    [onSave]
  );

  const isEditing = !!branchContact;

  return {
    form,
    handleSubmit,
    isEditing,
  };
};

/**
 * Hook for contact search and filtering
 */
export const useBranchContactSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<{
    role?: string;
    department?: string;
    language?: string;
    isActive?: boolean;
  }>({});

  const updateFilter = useCallback(
    (key: string, value: string | number | boolean) => {
      setFilters(prev => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    filters,
    updateFilter,
    clearFilters,
  };
};
